import React, { useEffect, useState, useRef, useCallback } from "react";
import api, { formatApiError } from "@/utils/api";
import { Section } from "@/components/dashboard/DashboardPrimitives";
import { Upload, Download, FileSpreadsheet, Loader2, CheckCircle, AlertCircle, Sparkles } from "lucide-react";

const TOOLS = [
  { id: "boneah", label: "BoneAH — Height & Weight", color: "#4ECDC4" },
  { id: "nin-nutriguide", label: "NIN-NutriGuide", color: "#FF6B6B" },
];

export default function BulkScreeningPanel() {
  const [tool, setTool] = useState("boneah");
  const [schema, setSchema] = useState(null);
  const [file, setFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [errors, setErrors] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/bulk-screening/jobs", { params: { limit: 20 } });
      setJobs(data.items || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadSchema = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/admin/bulk-screening/schema/${tool}`);
      setSchema(data);
    } catch (e) {
      console.error(e);
    }
  }, [tool]);

  useEffect(() => { loadSchema(); }, [loadSchema]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Poll active job every 2s
  useEffect(() => {
    if (!activeJob || activeJob.status === "completed" || activeJob.status === "failed") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/api/admin/bulk-screening/jobs/${activeJob.job_id}`);
        setActiveJob(data);
        if (data.status === "completed" || data.status === "failed") {
          loadJobs();
        }
      } catch (e) { /* keep polling */ }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeJob, loadJobs]);

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/api/admin/bulk-screening/template/${tool}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tots360_bulk_${tool}_template.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setErrors(null); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) { setFile(f); setErrors(null); }
  };

  const runBulk = async () => {
    if (!file) return;
    setRunning(true);
    setErrors(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/api/admin/bulk-screening/run/${tool}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setActiveJob({ ...data, processed: 0, failed: 0 });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await loadJobs();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.errors) {
        setErrors(detail);
      } else {
        setErrors({ message: formatApiError(e), errors: [] });
      }
    } finally {
      setRunning(false);
    }
  };

  const downloadZip = async (job) => {
    try {
      const r = await api.get(`/api/admin/bulk-screening/jobs/${job.job_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tots360_bulk_${job.tool}_${job.job_id}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const pct = activeJob && activeJob.total
    ? Math.round((activeJob.processed / activeJob.total) * 100)
    : 0;

  return (
    <Section title="Bulk Screening" description="Upload an Excel/CSV roster and auto-generate reports for many kids at once." testid="bulk-screening-section">
      {/* Tool selector */}
      <div className="flex gap-2 mb-4" data-testid="bulk-tool-tabs">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            data-testid={`bulk-tool-${t.id}`}
            onClick={() => { setTool(t.id); setFile(null); setErrors(null); }}
            className={`px-4 py-2 rounded-lg text-sm border transition-all ${
              tool === t.id
                ? "border-[#4ECDC4] bg-[#4ECDC4]/10 text-white"
                : "border-slate-700 text-slate-400 hover:text-white hover:border-slate-500"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Help & Template */}
      <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 p-5 mb-4">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h4 className="font-semibold text-slate-100 mb-1 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-[#4ECDC4]" /> Step 1 — Download template
            </h4>
            <p className="text-xs text-slate-400">
              Use the template's exact column headers. Max {schema?.max_rows || 200} rows per upload. Strict mode — any invalid row rejects the whole file.
            </p>
          </div>
          <button
            onClick={downloadTemplate}
            data-testid="bulk-download-template-btn"
            className="px-3 py-2 rounded bg-sky-500/10 border border-sky-500/40 text-sky-300 text-sm hover:bg-sky-500/20 flex items-center gap-2 whitespace-nowrap"
          >
            <Download className="w-4 h-4" /> Template
          </button>
        </div>
        {schema && (
          <details className="text-xs text-slate-400">
            <summary className="cursor-pointer text-slate-300 hover:text-white">Show expected columns ({schema.columns.length})</summary>
            <ul className="mt-2 space-y-1 pl-4">
              {schema.columns.map((c) => (
                <li key={c.key}>
                  <span className="text-slate-200">{c.header}</span>{" "}
                  <span className="text-slate-500">
                    ({c.type}
                    {Array.isArray(c.constraint) && typeof c.constraint[0] === "string" && (
                      <> — one of: {c.constraint.join(", ")}</>
                    )}
                    {Array.isArray(c.constraint) && typeof c.constraint[0] === "number" && (
                      <> — range {c.constraint[0]}..{c.constraint[1]}</>
                    )}
                    {c.constraint === "optional" && <> — optional</>}
                    )
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {/* Step 2: Upload */}
      <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 p-5 mb-4">
        <h4 className="font-semibold text-slate-100 mb-3 flex items-center gap-2">
          <Upload className="w-4 h-4 text-[#4ECDC4]" /> Step 2 — Upload filled file
        </h4>
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileRef.current?.click()}
          data-testid="bulk-upload-zone"
          className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center cursor-pointer hover:border-[#4ECDC4]/60 hover:bg-[#4ECDC4]/5 transition-all"
        >
          <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onPick} data-testid="bulk-file-input" />
          {file ? (
            <div className="text-sm">
              <FileSpreadsheet className="w-8 h-8 text-[#4ECDC4] mx-auto mb-2" />
              <p className="text-slate-100 font-medium">{file.name}</p>
              <p className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB · click to change</p>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <p className="text-sm text-slate-300">Drop .xlsx or .csv here or click to browse</p>
            </>
          )}
        </div>

        {errors && (
          <div className="mt-4 rounded-lg bg-rose-500/10 border border-rose-500/30 p-4" data-testid="bulk-errors">
            <p className="text-rose-300 text-sm font-semibold flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4" /> {errors.message}
            </p>
            {errors.errors && errors.errors.length > 0 && (
              <ul className="text-xs text-rose-200/80 space-y-1 max-h-40 overflow-y-auto pl-4 list-disc">
                {errors.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            {errors.total_errors > errors.errors?.length && (
              <p className="text-xs text-rose-300/60 mt-2">…and {errors.total_errors - errors.errors.length} more error(s).</p>
            )}
          </div>
        )}

        <button
          onClick={runBulk}
          disabled={!file || running}
          data-testid="bulk-run-btn"
          className="mt-4 w-full px-4 py-2.5 rounded-lg bg-[#4ECDC4] hover:bg-[#3dbdb5] disabled:opacity-50 disabled:cursor-not-allowed text-[#0a1628] font-semibold flex items-center justify-center gap-2"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {running ? "Validating…" : "Run Bulk Screening"}
        </button>
      </div>

      {/* Active job progress */}
      {activeJob && (
        <div className="rounded-xl bg-[#0E1A3A] border border-[#4ECDC4]/30 p-5 mb-4" data-testid="bulk-active-job">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-slate-100 flex items-center gap-2">
              {activeJob.status === "completed" ? (
                <CheckCircle className="w-4 h-4 text-emerald-400" />
              ) : (
                <Loader2 className="w-4 h-4 animate-spin text-[#4ECDC4]" />
              )}
              Job {activeJob.job_id.slice(-8)} — {activeJob.status}
            </h4>
            <span className="text-xs text-slate-400">{activeJob.processed} / {activeJob.total} · {activeJob.failed || 0} failed</span>
          </div>
          <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#4ECDC4] transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="bulk-progress-bar"
            />
          </div>
          {activeJob.status === "completed" && (
            <button
              onClick={() => downloadZip(activeJob)}
              data-testid="bulk-download-zip-btn"
              className="mt-4 w-full px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 font-semibold hover:bg-emerald-500/25 flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" /> Download ZIP ({activeJob.total} reports + analysis)
            </button>
          )}
        </div>
      )}

      {/* Job history */}
      <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 overflow-hidden">
        <div className="p-3 border-b border-slate-800 text-xs text-slate-400 uppercase tracking-wide">Past jobs</div>
        {jobs.length === 0 ? (
          <p className="p-6 text-sm text-slate-500 text-center">No bulk screening jobs yet.</p>
        ) : (
          <div className="divide-y divide-slate-800/80">
            {jobs.map((j) => (
              <div key={j.job_id} className="p-3 flex items-center justify-between gap-4 text-sm" data-testid={`job-row-${j.job_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-100 font-medium truncate">{j.filename || j.job_id}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {j.tool === "boneah" ? "BoneAH H/W" : "NIN-NutriGuide"} · {j.total} rows · {j.failed || 0} failed · {j.created_at ? new Date(j.created_at).toLocaleString() : ""}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  j.status === "completed" ? "bg-emerald-500/15 text-emerald-300" :
                  j.status === "failed" ? "bg-rose-500/15 text-rose-300" :
                  "bg-amber-500/15 text-amber-300"
                }`}>{j.status}</span>
                {j.status === "completed" && (
                  <button
                    onClick={() => downloadZip(j)}
                    data-testid={`job-download-${j.job_id}`}
                    className="px-2.5 py-1 rounded bg-sky-500/15 hover:bg-sky-500/25 text-sky-300 text-xs flex items-center gap-1"
                  >
                    <Download className="w-3 h-3" /> ZIP
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}
